// DEPENDENCIES ---------------------------------------------------------------- //

import React from 'react';
import { Map } from '@vis.gl/react-google-maps';

import { Locations } from '@gql/base-types';

import { css } from '@emotion/css';
import { Theme, useTheme, Button, Chip, CircularProgress, Typography } from '@mui/material';
import { BiRefresh, BiSolidMapPin } from 'react-icons/bi';
import { MdPlayCircle, MdFlagCircle, MdWarningAmber } from 'react-icons/md';

import { GoogleMapsElement, MapControls, MapNavigation, useMapPreferences, useMapRoute } from '@components/Maps';
import { useResizeHandle } from '@features/moves/map/useResizeHandle';
import MapLocationSelectorMarker from '@features/moveCreation/components/MapLocationSelector/MapLocationSelectorMarker';
import type { MapLocationMarker } from '@features/moveCreation/components/MapLocationSelector/types';
import { getDefaultBounds } from '@components/DriverTracker/driverTrackerService';
import {
  capitalizeLaneRole,
  getLaneRoleLocationLabel,
  type LaneRoleLabel,
  type PlannerLaneMapProps,
} from '@hopdrive/feature-move-planner';

// TYPES ---------------------------------------------------------------- //

/** The props the planner's lane form hands its host's lane map. */
type MovePlannerLaneMapProps = PlannerLaneMapProps;

// COMPONENT ---------------------------------------------------------------- //

const MovePlannerLaneMap = ({
  locations = [],
  pickup,
  delivery,
  customerLocation,
  laneRoles,
  onReset,
  onPickupSelect,
  onDeliverySelect,
  onPickupDeselect,
  onDeliveryDeselect,
}: MovePlannerLaneMapProps) => {
  const pickupRole: LaneRoleLabel = laneRoles?.pickup ?? 'pickup';
  const deliveryRole: LaneRoleLabel = laneRoles?.delivery ?? 'delivery';
  const pickupBubbleTitle = laneRoles ? getLaneRoleLocationLabel(pickupRole) : undefined;
  const deliveryBubbleTitle = laneRoles ? getLaneRoleLocationLabel(deliveryRole) : undefined;
  const theme = useTheme();
  const cls = useStyles(theme);
  const [routeLoading, setRouteLoading] = React.useState(false);
  const [routeError, setRouteError] = React.useState<string | null>(null);

  const hasPickupCoords = pickup?.latitude != null && pickup?.longitude != null;
  const hasDeliveryCoords = delivery?.latitude != null && delivery?.longitude != null;
  const routeEnabled = !!pickup?.id && !!delivery?.id && hasPickupCoords && hasDeliveryCoords;

  useMapRoute({
    mapId: 'lane-planner-map',
    pickup: hasPickupCoords ? { lat: pickup!.latitude!, lng: pickup!.longitude! } : null,
    delivery: hasDeliveryCoords ? { lat: delivery!.latitude!, lng: delivery!.longitude! } : null,
    enabled: routeEnabled,
    onLoadingChange: setRouteLoading,
    onError: setRouteError,
  });

  const { mode: mapMode, mapStyles: activeMapStyles } = useMapPreferences();
  const overlayText = {
    primary: mapMode === 'light' ? theme?.palette?.text?.primary : theme?.palette?.text?.contrastPrimary,
    secondary: mapMode === 'light' ? theme?.palette?.text?.secondary : theme?.palette?.text?.contrastSecondary,
    disabled: mapMode === 'light' ? theme?.palette?.text?.disabled : theme?.palette?.text?.contrastDisabled,
  };
  const mapResize = useResizeHandle({
    initial: 480,
    min: 256,
    max: 768,
    direction: 'vertical',
    storageKey: 'lane-planner-map-height',
  });

  // Determine the current state
  const hasPickup = !!pickup?.id;
  const hasDelivery = !!delivery?.id;

  let mapState: 'needsPickup' | 'needsDelivery' | 'complete';
  if (!hasPickup) {
    mapState = 'needsPickup';
  } else if (!hasDelivery) {
    mapState = 'needsDelivery';
  } else {
    mapState = 'complete';
  }

  // Convert locations to map markers. Shows the customer's default location,
  // pickup, delivery, and any favorited locations for the customer.
  const allMarkers: MapLocationMarker[] = React.useMemo(() => {
    const pinnedIds = new Set([customerLocation?.id, pickup?.id, delivery?.id].filter(Boolean));

    return locations
      .filter((loc): loc is Locations => loc !== null && loc.latitude !== null && loc.longitude !== null)
      .filter(loc => pinnedIds.has(loc.id) || loc.favorite)
      .map(loc => ({
        id: `location-${loc.id}`,
        name: loc.name || 'Unknown Location',
        address: loc.address || 'Unknown Address',
        position: {
          lat: loc.latitude!,
          lng: loc.longitude!,
        },
        favorite: loc.favorite,
        location: loc,
      }));
  }, [locations, customerLocation?.id, pickup?.id, delivery?.id]);

  // Get initial map position — center on customer's default location if available, otherwise fit all markers
  const getInitialProps = (): {
    defaultBounds?: google.maps.LatLngBoundsLiteral;
    defaultCenter?: google.maps.LatLngLiteral;
    defaultZoom?: number;
  } => {
    if (customerLocation?.latitude && customerLocation?.longitude) {
      return { defaultCenter: { lat: customerLocation.latitude, lng: customerLocation.longitude }, defaultZoom: 10 };
    }
    return { defaultBounds: getDefaultBounds(allMarkers) };
  };

  // Get instruction text based on state
  const getInstructionText = () => {
    switch (mapState) {
      case 'needsPickup':
        return `Select a ${pickupRole} location using the input below or click a favorite on the map.`;
      case 'needsDelivery':
        return `Select a ${deliveryRole} location using the input below or click a favorite on the map.`;
      case 'complete':
        return '';
      default:
        return '';
    }
  };

  // Handle marker click based on current state
  const handleMarkerClick = (marker: MapLocationMarker) => {
    if (!marker.location) return;

    // Concierge rule: the rooftop's default location is always the service
    // endpoint. Force-route its click to whichever slot carries the 'service'
    // lane role, short-circuiting the standard needsPickup/needsDelivery flow.
    const isRooftopDefault = customerLocation?.id != null && marker.location.id === customerLocation.id;
    const serviceSlot: 'pickup' | 'delivery' | null =
      laneRoles?.pickup === 'service' ? 'pickup' : laneRoles?.delivery === 'service' ? 'delivery' : null;

    if (isRooftopDefault && serviceSlot) {
      if (serviceSlot === 'pickup') {
        if (pickup?.id === marker.location.id) onPickupDeselect?.();
        else onPickupSelect?.(marker.location);
      } else {
        if (delivery?.id === marker.location.id) onDeliveryDeselect?.();
        else onDeliverySelect?.(marker.location);
      }
      return;
    }

    switch (mapState) {
      case 'needsPickup':
        // Clicking the delivery marker deselects it
        if (marker.location.id === delivery?.id) {
          if (onDeliveryDeselect) onDeliveryDeselect();
        } else if (onPickupSelect) {
          onPickupSelect(marker.location);
        }
        break;
      case 'needsDelivery':
        // Clicking the pickup marker deselects it
        if (marker.location.id === pickup?.id) {
          if (onPickupDeselect) onPickupDeselect();
        } else if (onDeliverySelect) {
          onDeliverySelect(marker.location);
        }
        break;
      case 'complete':
        // Clicking a selected marker deselects it
        if (marker.location.id === pickup?.id) {
          if (onPickupDeselect) onPickupDeselect();
        } else if (marker.location.id === delivery?.id) {
          if (onDeliveryDeselect) onDeliveryDeselect();
        } else {
          // Clicking an unselected marker replaces delivery
          if (onDeliverySelect) onDeliverySelect(marker.location);
        }
        break;
    }
  };

  return (
    <div className={cls.mapContainer} style={{ height: mapResize.size }}>
      <Map
        id='lane-planner-map'
        {...getInitialProps()}
        styles={activeMapStyles}
        clickableIcons={false}
        gestureHandling='cooperative'
        disableDefaultUI
      >
        {/* Show markers based on state */}
        {allMarkers
          .filter(marker => {
            const isPickup = pickup?.id === marker.location?.id;
            const isDelivery = delivery?.id === marker.location?.id;

            // In complete state, only show pickup and delivery markers
            if (mapState === 'complete') {
              return isPickup || isDelivery;
            }

            // In other states, show all markers
            return true;
          })
          .map((marker, i) => {
            const isPickup = pickup?.id === marker.location?.id;
            const isDelivery = delivery?.id === marker.location?.id;
            const isSelected = isPickup || isDelivery;

            // Compute hint text based on current state and marker role
            let hintText: string | undefined;
            if (mapState === 'needsPickup') {
              hintText = isDelivery ? 'Click to deselect delivery' : 'Click to select as pickup';
            } else if (mapState === 'needsDelivery') {
              hintText = isPickup ? 'Click to deselect pickup' : 'Click to select as delivery';
            } else if (mapState === 'complete') {
              if (isPickup) hintText = 'Click to deselect pickup';
              else if (isDelivery) hintText = 'Click to deselect delivery';
            }

            let bubbleTitle: string | undefined;
            if (isPickup) bubbleTitle = pickupBubbleTitle;
            else if (isDelivery) bubbleTitle = deliveryBubbleTitle;
            else if (mapState === 'needsPickup') bubbleTitle = `Select as ${capitalizeLaneRole(pickupRole)} Location`;
            else if (mapState === 'needsDelivery')
              bubbleTitle = `Select as ${capitalizeLaneRole(deliveryRole)} Location`;

            return (
              <MapLocationSelectorMarker
                key={`lane-marker-${i}`}
                mapId='lane-planner-map'
                marker={{
                  ...marker,
                  id: isPickup ? 'pickup' : isDelivery ? 'delivery' : marker.favorite ? 'favorite' : marker.id,
                }}
                selected={isSelected}
                onMarkerClick={handleMarkerClick}
                isLaneComplete={mapState === 'complete'}
                hintText={hintText}
                deselectable={isSelected}
                bubbleTitle={bubbleTitle}
              />
            );
          })}
      </Map>

      {/* Empty state — centered message when no markers exist */}
      {allMarkers.length === 0 && mapState !== 'complete' && (
        <GoogleMapsElement style={{ top: '50%', left: '50%', transform: 'translate(-50%, -50%)', textAlign: 'center' }}>
          <BiSolidMapPin size={24} style={{ color: overlayText.disabled, marginBottom: 4 }} />
          <Typography fontWeight={600} variant='body2' sx={{ color: overlayText.primary }}>
            No locations on map
          </Typography>
          <Typography variant='caption' sx={{ color: overlayText.secondary }}>
            Use the location fields below to search for and add pickup and delivery locations.
          </Typography>
        </GoogleMapsElement>
      )}

      {/* Floating directions panel — top left */}
      {mapState !== 'complete' && allMarkers.length > 0 ? (
        <GoogleMapsElement style={{ top: 10, left: 10, maxWidth: 640 }}>
          <Typography variant='body2' sx={{ color: overlayText.primary }}>
            {getInstructionText()}
          </Typography>
        </GoogleMapsElement>
      ) : mapState === 'complete' ? (
        <GoogleMapsElement style={{ top: 10, left: 10, maxWidth: 640 }}>
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center' }}>
            {hasPickup && (
              <Chip
                size='small'
                color='info'
                icon={<MdPlayCircle />}
                label={pickup?.name || 'Pickup'}
                onDelete={onPickupDeselect}
              />
            )}
            <Typography fontSize={14} sx={{ color: overlayText.secondary }}>
              to
            </Typography>
            {hasDelivery && (
              <Chip
                size='small'
                color='success'
                icon={<MdFlagCircle />}
                label={delivery?.name || 'Delivery'}
                onDelete={onDeliveryDeselect}
              />
            )}
            {onReset && (
              <Button
                variant='contained'
                size='small'
                color='utility'
                startIcon={<BiRefresh />}
                onClick={onReset}
                style={{ marginLeft: '8px' }}
              >
                Reset
              </Button>
            )}
          </div>
        </GoogleMapsElement>
      ) : null}

      {/* Route loading indicator — stacked above the style switch */}
      {routeLoading && (
        <GoogleMapsElement style={{ bottom: 60, left: 10 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <CircularProgress size={16} />
            <Typography variant='body2' sx={{ color: overlayText.primary }}>
              Loading route...
            </Typography>
          </div>
        </GoogleMapsElement>
      )}

      {/* Route error indicator — ZERO_RESULTS or other DirectionsService failure */}
      {!routeLoading && routeError && routeEnabled && (
        <GoogleMapsElement style={{ bottom: 60, left: 10 }}>
          <Chip
            size='small'
            color='warning'
            icon={<MdWarningAmber />}
            label={routeError === 'ZERO_RESULTS' ? 'No drivable route between these locations' : 'Could not load route'}
          />
        </GoogleMapsElement>
      )}

      {/* Shared map controls (fullscreen + light/dark + focus) — top right */}
      <MapControls />
      <MapNavigation mapId='lane-planner-map' />

      {/* Resize drag handle */}
      <div className={cls.resizeHandle} {...mapResize.handleProps} />
    </div>
  );
};

// STYLES ---------------------------------------------------------------- //

const useStyles = (theme?: Theme) => ({
  mapContainer: css`
    position: relative;
    display: block;
    width: 100%;
    border: 1px solid ${theme?.palette?.divider};
    border-radius: 10px;
    margin-bottom: 18px;
    background-color: ${theme?.palette?.background?.paper};
    overflow: hidden;
  `,
  resizeHandle: css`
    position: absolute;
    bottom: 0;
    left: 0;
    right: 0;
    height: 6px;
    cursor: row-resize;
    z-index: 2;
    transition: background-color 0.2s ease;

    &:hover {
      background-color: ${theme?.palette?.primary?.main}80;
    }
  `,
});

// EXPORT ---------------------------------------------------------------- //

export default MovePlannerLaneMap;
